/* eslint-disable react-hooks/exhaustive-deps */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import * as Yup from "yup";

import {
  RIAddressInfoForm,
  RIIncomeDetailsForm,
  RINetTaxableIncomeForm,
  RIOwnerDetailsForm,
  RISelfPropertyTypeForm,
  RITaxSavingDeductionForm,
  RITenantDetailsForm
} from "..";
import { PropertTypes, RIHousePropertTypes } from "../../../../config/filingConstants";
import { IFormITR1, IGivenOnRentProperties } from "../../../../config/interfaces";
import {
  AddressFormInitValues,
  PropertiesOnRentFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";

type Props = {
  showCanvas: boolean;
  formValues: IGivenOnRentProperties | null;
  handleCloseBtnPress: (salaryData: IGivenOnRentProperties | null) => void;
};

const RIPropertiesOnRentDetailsForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress, formValues } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const ITR1 = useFormikContext<IFormITR1>();

  const fullname = `${ITR1.values?.FirstName || ""} ${ITR1.values?.MiddleName || ""} ${
    ITR1.values?.SurNameOrOrgName || ""
  }`;

  const FormInitValues: IGivenOnRentProperties = {
    PropertyType: RIHousePropertTypes[0],
    IsSameAddress: false,
    Address: { ...AddressFormInitValues },
    OwnerDetails: [
      {
        FullName: fullname,
        Pan: ITR1.values.PAN,
        Percentage: "100"
      }
    ],

    TenantDetails: [
      {
        TenantName: "",
        Aadhaar: "",
        Pan: ""
      }
    ],
    TotalAnnualRentReceived: "",
    MunicipalTax: "",
    NetIncome: "",
    StandardDeductionUs24A: "",
    FinancialYear: "",
    InterestPreConstruction: "",
    InterestPostConstruction: "",
    TotalDeduction: "",
    NetTaxableIncome: ""
  };

  const [initFormValues, setInitFormValues] = useState<IGivenOnRentProperties>(FormInitValues);
  const [selfPropertiesCount, setSelfPropertiesCount] = useState<number>(0);

  useEffect(() => {
    const selfProperties =
      ITR1.values?.IncomeDetails?.RISelfOccupiedProperties?.map(
        (item) => item.PropertyType === PropertTypes.SELF
      ) || [];
    setSelfPropertiesCount(() => selfProperties.length);

    if (showCanvas && formValues) {
      setInitFormValues(() => cloneDeep(formValues));
    } else {
      setInitFormValues(() => cloneDeep(FormInitValues));
    }
  }, [showCanvas, formValues]);

  return (
    <>
      <Offcanvas show={showCanvas} placement="end" backdrop={true}>
        <Formik
          enableReinitialize={true}
          initialValues={initFormValues}
          validationSchema={Yup.object().shape({ ...PropertiesOnRentFormSchema })}
          onSubmit={(values) => {
            handleCloseBtnPress(values);
          }}
        >
          {({ handleSubmit, validateForm, submitCount, isValid, errors, values }) => (
            <>
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
              <Offcanvas.Header>
                <Offcanvas.Title>
                  Add/Edit Rental Property details
                  <small>
                    Enter the total values (such as Rent, Interest & others) & we will calculate
                    values as per your share if the house is co-owned
                  </small>
                </Offcanvas.Title>
                <Button
                  variant="primary"
                  className="icon-only hide-offcanvas"
                  onClick={() => handleCloseBtnPress(null)}
                >
                  <FASvgIcon icon={faXmark} />
                </Button>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <RISelfPropertyTypeForm
                  initialValues={FormInitValues}
                  isDisabled={selfPropertiesCount >= 2 && !formValues}
                />
                <RIAddressInfoForm formValues={ITR1.values.Address} />
                <RIOwnerDetailsForm />
                <RITenantDetailsForm />
                <RIIncomeDetailsForm />
                <RITaxSavingDeductionForm />
                <RINetTaxableIncomeForm />
              </Offcanvas.Body>

              <div className="offcanvas-btns">
                {!isFilingReadOnly ? (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={async (event) => {
                      event.preventDefault();
                      await validateForm();
                      let sum = values.OwnerDetails.slice(1).reduce(
                        (acc, item) => acc + (parseInt(item.Percentage) || 0),
                        0
                      );
                      const firstOwnerPercentage = 100 - sum;
                      if (sum > 100 || firstOwnerPercentage === 0) return;

                      handleSubmit();
                    }}
                  >
                    Save and Close
                  </Button>
                ) : (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => handleCloseBtnPress(null)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </Formik>
      </Offcanvas>
    </>
  );
};

export default RIPropertiesOnRentDetailsForm;
